import useDeleteAccountTerm from '@/api/mutations/account/optimistic/useDeleteAccountTerm';
import meKeys from '@/api/queries/me/keyFactory';
import useGetMyAccountTerms from '@/api/queries/me/useGetMyAccountTerms';
import {
  Button,
  Card,
  Modal,
  ModalProps,
  RichTextarea,
  SkeletonLoader,
  useToastContext,
} from '@payaca/ambrosia';

import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';

export interface IProps extends Omit<ModalProps, 'title'> {
  termId?: string;
}

const DeleteTermModal: FC<IProps> = (props) => {
  const { onClose, termId, ...rest } = props;

  const { pushError } = useToastContext();

  /**
   * Queries
   */
  const queryClient = useQueryClient();
  const { terms } = useGetMyAccountTerms({
    types: ['WRITTEN'],
  });

  /**
   * Mutations
   */
  const { mutateAsync: deleteAccountTerm, isPending: isLoading } =
    useDeleteAccountTerm();

  const handleDelete = async () => {
    if (!termId) {
      return;
    }

    await deleteAccountTerm({ termId }).catch(() => {
      pushError('Failed to delete term');
    });

    await queryClient.invalidateQueries({
      queryKey: meKeys.accountTerms(),
    });

    onClose?.();
  };

  const foundTerm = terms?.find((term) => {
    return term.__typename === 'WrittenTerm' && term.id === termId;
  });

  return (
    <Modal title="Delete term" onClose={onClose} {...rest}>
      {foundTerm ? (
        <>
          <Modal.Body>
            <p className="mb-4">
              Are you sure you want to delete this term? This action cannot be
              undone.
            </p>

            <Card>
              <Card.Body>
                <RichTextarea
                  value={'content' in foundTerm ? foundTerm.content : ''}
                  readOnly
                />
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button
                onClick={onClose}
                disabled={isLoading}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                isProcessing={isLoading}
                disabled={isLoading}
                onClick={handleDelete}
                colour="negative"
              >
                Delete
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </>
      ) : (
        <Modal.Body>
          <SkeletonLoader.Input />
        </Modal.Body>
      )}
    </Modal>
  );
};

export default DeleteTermModal;
